import { useState } from "react";
import { ArrowLeft, Phone, Loader2, ArrowRight, AlertTriangle, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { OTP_TRANSACTIONAL_NOTICE } from "@/lib/phone";

interface PhoneSignInProps {
  onBack: () => void;
}

type Step = "phone" | "code" | "complete-signup";

// Cap OTP send attempts per session to defend against Twilio outages and
// prevent runaway spend. Once tripped, users get a hard fallback CTA.
const MAX_SEND_ATTEMPTS = 3;

const PhoneSignIn = ({ onBack }: PhoneSignInProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupToken, setSignupToken] = useState<string | null>(null);
  const [sendAttempts, setSendAttempts] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);




  const callPhoneAuth = async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke("phone-auth", { body });
    type PhoneAuthErrorBody = {
      error?: string;
      twilio_message?: string;
      twilioError?: string;
      twilio_status?: number;
      checked_verification_sid?: string | null;
      service_sid?: string | null;
    };
    const formatMsg = (p: PhoneAuthErrorBody): string | null => {
      const msg = p.error ?? p.twilioError;
      if (!msg) return null;
      const detail = p.twilio_message;
      if (p.twilio_status === 404 && p.checked_verification_sid) {
        return `${msg} [checked ${p.checked_verification_sid} on ${p.service_sid ?? "unknown"}]${detail ? ` (${detail})` : ""}`;
      }
      return detail ? `${msg} (${detail})` : msg;
    };
    if (error) {
      const ctx = (error as { context?: Response }).context;
      const parsed = ctx
        ? ((await ctx.clone().json().catch(() => null)) as PhoneAuthErrorBody | null)
        : null;
      const built = parsed ? formatMsg(parsed) : null;
      if (built) throw new Error(built);
      throw new Error(error.message);
    }
    const d = data as PhoneAuthErrorBody | null;
    if (d?.error) {
      throw new Error(formatMsg(d) ?? d.error);
    }
    return data as Record<string, unknown>;
  };

  const sendCode = async () => {
    if (!phone.trim()) {
      toast({ title: "Enter your phone number", variant: "destructive" });
      return;
    }
    if (sendAttempts >= MAX_SEND_ATTEMPTS) {
      setLastError("Too many attempts. Please use email or Google sign-in instead.");
      return;
    }
    setLoading(true);
    setLastError(null);
    try {
      const result = await callPhoneAuth({ phone, action: "send", fullName: fullName || undefined });
      // Only advance if the backend confirms a usable pending verification.
      if (!result || (result as { status?: string }).status === "failed") {
        throw new Error("SMS provider did not confirm delivery. Please try email sign-in.");
      }
      setSendAttempts((n) => n + 1);
      setStep("code");
      toast({ title: "Code sent 📱", description: "Check your messages." });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      setSendAttempts((n) => n + 1);
      setLastError(msg);
      console.warn("[phone-auth] send failed", { attempts: sendAttempts + 1, msg });
      toast({
        title: "Couldn't send code",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  const verifyCode = async () => {
    if (code.length < 6) {
      toast({ title: "Enter the 6-digit code", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      // Tell the edge function where to land the magic link so the redirect
      // hits our /auth/callback route, which confirms the session and then
      // routes onward (or shows an error if it never materializes).
      const callbackUrl = `${window.location.origin}/auth/callback`;
      const result = await callPhoneAuth({
        phone,
        action: "verify",
        code,
        redirectTo: callbackUrl,
      });
      if (result.isNewUser) {
        setSignupToken((result.signupToken as string | undefined) ?? null);
        setStep("complete-signup");

      } else if (result.magicLink) {
        // Existing user: navigate to magic link to establish session.
        // After the link is consumed, the browser lands on /auth/callback.
        window.location.href = result.magicLink as string;
      } else {
        toast({ title: "Verified", description: "Signing you in…" });
        navigate("/auth/callback", { replace: true });
      }
    } catch (e) {
      toast({
        title: "Verification failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const completeSignup = async () => {
    if (!email.trim() || !password.trim()) {
      toast({ title: "Email and password required", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await callPhoneAuth({
        phone,
        action: "complete-signup",
        email,
        password,
        fullName,
        signupToken,
      });

      // Sign in with the new credentials
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;
      toast({ title: "Welcome to BailBuddies! 👻" });
      navigate("/");
    } catch (e) {
      toast({
        title: "Couldn't finish signup",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {step === "phone" && (
        <>
          <div className="space-y-2">
            <Label htmlFor="ph-phone">Phone number</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="ph-phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="pl-10 h-12 rounded-xl"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ph-name">Name (new accounts)</Label>
            <Input
              id="ph-name"
              placeholder="Your name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="h-12 rounded-xl"
            />
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed px-1">{OTP_TRANSACTIONAL_NOTICE}</p>
          <Button
            onClick={sendCode}
            disabled={loading || !phone.trim()}
            className="w-full h-12 rounded-xl bg-primary hover:from-purple-700 hover:via-blue-600 hover:to-pink-600 font-display text-lg"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <span className="flex items-center gap-2">Send code <ArrowRight className="w-5 h-5" /></span>
            )}
          </Button>
          {lastError && (
            <div role="alert" className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 space-y-2">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{lastError}</span>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                className="w-full h-10 rounded-lg"
              >
                <Mail className="w-4 h-4 mr-2" /> Use email or Google instead
              </Button>
            </div>
          )}
          <Button variant="ghost" onClick={onBack} className="w-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </>
      )}

      {step === "code" && (
        <>
          <div className="space-y-2">
            <Label htmlFor="ph-code">Enter the 6-digit code sent to {phone}</Label>
            <Input
              id="ph-code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="h-12 rounded-xl text-center text-2xl tracking-widest"
            />
          </div>
          <Button
            onClick={verifyCode}
            disabled={loading || code.length < 6}
            className="w-full h-12 rounded-xl"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify"}
          </Button>
          <Button variant="ghost" onClick={() => setStep("phone")} className="w-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Use a different number
          </Button>
        </>
      )}

      {step === "complete-signup" && (
        <>
          <p className="text-sm text-muted-foreground">
            Finish creating your account. We'll use this email for sign-in and notifications.
          </p>
          <div className="space-y-2">
            <Label htmlFor="ph-email">Email</Label>
            <Input
              id="ph-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-12 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ph-pass">Password</Label>
            <Input
              id="ph-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="h-12 rounded-xl"
            />
          </div>
          <Button
            onClick={completeSignup}
            disabled={loading || !email.trim() || !password.trim()}
            className="w-full h-12 rounded-xl"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create account"}
          </Button>
        </>
      )}
    </div>
  );
};

export default PhoneSignIn;
